import type { ShopPreferencesUserPortalDto } from "@babackapp/private-api-client";
import { pickLocalizedText } from "@babackapp/return-widget-components/utils/pick-localized-text";

type CustomTextField = keyof Pick<
  ShopPreferencesUserPortalDto,
  "notFullFilled" | "notCompliantPOS" | "notCompliant" | "countryNotAvailable" | "orderExpired"
>;

const ERROR_CUSTOM_FIELD: Record<string, CustomTextField | undefined> = {
  "order-not-fulfilled": "notFullFilled",
  "pos-order-not-supported": "notCompliantPOS",
  "order-not-supported": "notCompliant",
  "country-not-supported": "countryNotAvailable",
  "order-not-found": undefined,
  "order-expired": "orderExpired",
  // Shopify is down, not the shopper's fault: no merchant override, since this is
  // never a shop configuration matter. Without this entry the code fell through to
  // "unknown", which reads "we couldn't find your order" — the wrong answer, and the
  // main driver of support calls during a Shopify incident.
  "shopify-unavailable": undefined,
  unknown: undefined,
};

function getErrorCode(error: unknown): string {
  if (error && typeof error === "object" && "code" in error) {
    const { code } = error as { code?: unknown };
    if (typeof code === "string" && code in ERROR_CUSTOM_FIELD) return code;
  }
  return "unknown";
}

type ResolveParams = {
  error: unknown;
  t: (key: string, params?: Record<string, unknown>) => string;
  userPortal: ShopPreferencesUserPortalDto | undefined;
  language: string;
};

export type OrderIdentificationError = {
  title: string;
  description: string;
  html: string | null;
};

export function resolveOrderIdentificationError({
  error,
  t,
  userPortal,
  language,
}: ResolveParams): OrderIdentificationError {
  const code = getErrorCode(error);
  const customField = ERROR_CUSTOM_FIELD[code];
  const customTexts = customField ? userPortal?.[customField] : undefined;

  return {
    title: t(`orderIdentification.error.${code}.title`),
    description: t(`orderIdentification.error.${code}.description`),
    html: customTexts ? pickLocalizedText(customTexts, language) : null,
  };
}
